<!--
  The application shell.

  Two things are load-bearing here and are not allowed to move:

    * The on-air state is in the top bar, on every route. An operator changing a
      podcast setting still needs to know the station just fell back.
    * Emergency, fallback and offline raise a banner above the routed page that no
      breakpoint collapses (design system section 2).
-->
<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted } from 'vue';
import { RouterLink } from 'vue-router';
import { useI18n } from 'vue-i18n';
import {
  ShioBanner,
  ShioNeonClock,
  ShioOnAirBadge,
  ICONS,
  ON_AIR_PRESENTATION,
} from '@shiocast/ui';
import NavRail from '@/components/NavRail.vue';
import NowPlayingBar from '@/components/NowPlayingBar.vue';
import { useStationStore } from '@/stores/station';
import { useStationsStore } from '@/stores/stations';
import { useAuthStore } from '@/stores/auth';
import { COMPONENT_LABELS } from '@/components/component-labels';
import LanguageSwitcher from '@/components/LanguageSwitcher.vue';
import StationSwitcher from '@/components/StationSwitcher.vue';

const station = useStationStore();
const stations = useStationsStore();
const auth = useAuthStore();

const { t } = useI18n();

const presentation = computed(() => ON_AIR_PRESENTATION[station.mode]);

const problemTitle = computed(() =>
  // A pluralised message rather than an assembled sentence: Dutch and English do not
  // agree on where the count goes, and concatenation would strand one of them.
  t('shell.componentsNeedAttention', { count: station.problems.length }, station.problems.length),
);

/** Component names an operator recognises, not the enum values the API uses. */
const problemNames = computed(() =>
  station.problems
    .map((problem) => COMPONENT_LABELS[problem.component] ?? problem.component)
    .join(', '),
);
const stationName = computed(() => station.nowPlaying?.station.name ?? '');

const initial = computed(() =>
  (auth.user?.display_name ?? auth.user?.email ?? '?').trim().charAt(0).toUpperCase(),
);

/** The role in the operator's language, not the enum the API uses. */
const roleLabel = computed(() => (auth.user ? t(`users.role.${auth.user.role}`) : ''));

/**
 * Nothing is asked of the API until the station is known.
 *
 * The shell can mount before the router guard has settled which station this operator
 * is working in, and a poll that goes out first is a poll for no station: it is refused,
 * and the panel opens on a wall of errors it then corrects a second later. Waiting is
 * the whole fix, and it costs one round trip on a page the operator is still reading.
 */
onMounted(async () => {
  if (!stations.loaded) {
    await stations.load().catch(() => {
      // An account with no station still gets a shell; the screens that need one say so.
    });
  }
  station.start();
});
onBeforeUnmount(() => station.stop());
</script>

<template>
  <div class="shio-shell">
    <!--
      A skip link, because the navigation rail is roughly twenty tab stops deep and the
      emergency controls live on the routed page. During an incident an operator must
      not have to tab through the whole product to reach them.
    -->
    <a class="shio-skip" href="#shio-main">{{ t('shell.skipToControls') }}</a>

    <NavRail class="shio-shell__rail" />

    <div class="shio-shell__main">
      <header class="shio-shell__top">
        <div class="shio-shell__identity">
          <span class="shio-shell__mark shio-wordmark">Shio<b>Cast</b></span>
          <span class="shio-body-sm">{{ stationName }}</span>
        </div>

        <!--
          The station clock, as V1 had it. Centred because it is the one element in the
          header that is read constantly, and it reads in the station's timezone.
        -->
        <ShioNeonClock
          :timezone="station.timezone"
          :connected="!station.stale"
          :label="t('shell.stationTime')"
        />

        <div class="shio-shell__state">
          <ShioOnAirBadge :mode="station.mode" />
          <StationSwitcher />
          <LanguageSwitcher />
          <!--
            The name opens the account, it does not sign out. Those were the same
            control until two-factor authentication gave the account a page of its own,
            and a header chip that logs you out on a mis-click is the wrong default
            during a broadcast.
          -->
          <RouterLink
            class="shio-btn shio-btn--ghost shio-shell__user"
            :to="{ name: 'account-security' }"
            :title="t('shell.yourAccount')"
          >
            <span class="shio-shell__avatar" aria-hidden="true">{{ initial }}</span>
            <span class="shio-shell__who">
              <span class="shio-shell__name">{{
                auth.user?.display_name ?? auth.user?.email
              }}</span>
              <!-- The role, because what an operator may do differs by it and the header
                   is where they check who they are signed in as. -->
              <span class="shio-shell__role">{{ roleLabel }}</span>
            </span>
          </RouterLink>
          <button
            class="shio-btn shio-btn--ghost shio-shell__signout"
            type="button"
            :title="t('common.signOut')"
            :aria-label="t('common.signOut')"
            @click="auth.logout()"
          >
            <component :is="ICONS.LogOut" :size="18" aria-hidden="true" />
          </button>
        </div>
      </header>

      <main id="shio-main" class="shio-shell__content" tabindex="-1">
        <!--
          The panel cannot see the API. The stream may well still be on air, so this
          says exactly that rather than claiming the station is offline.
        -->
        <ShioBanner
          v-if="station.stale"
          tone="warning"
          icon="WifiOff"
          :title="t('shell.apiUnreachable')"
        >
          {{ t('shell.apiUnreachableDetail') }}
        </ShioBanner>

        <ShioBanner
          v-else-if="presentation.raisesBanner"
          :tone="presentation.bannerTone ?? 'warning'"
          :icon="presentation.icon"
          :title="presentation.announcement"
        >
          <template v-if="station.mode === 'OFFLINE'">
            {{ t('shell.offlineDetail') }}
          </template>
        </ShioBanner>

        <ShioBanner
          v-if="station.problems.length > 0"
          tone="danger"
          icon="TriangleAlert"
          :title="problemTitle"
        >
          {{ problemNames }}
        </ShioBanner>

        <slot />
      </main>

      <NowPlayingBar class="shio-shell__player" />
    </div>
  </div>
</template>

<style scoped>
.shio-skip {
  position: absolute;
  left: var(--shio-space-3);
  top: -100px;
  z-index: var(--shio-z-banner);
  padding: var(--shio-space-2) var(--shio-space-4);
  background: hsl(var(--shio-accent-primary));
  color: hsl(var(--shio-on-accent));
  border-radius: var(--shio-radius-sm);
  font-weight: 600;
  text-decoration: none;
  transition: top var(--shio-motion-quick) var(--shio-ease);
}

.shio-skip:focus {
  top: var(--shio-space-3);
}

/*
 * A fixed viewport height, not a minimum. The header and the player bar must never
 * scroll away: on-air state and the monitor control are the two things an operator has to
 * be able to reach at any moment, so the page scrolls inside the content area instead.
 */
.shio-shell {
  display: grid;
  grid-template-columns: auto 1fr;
  height: 100vh;
  height: 100dvh;
  overflow: hidden;
}

.shio-shell__main {
  display: grid;
  grid-template-rows: var(--shio-topbar-height) minmax(0, 1fr) var(--shio-nowplaying-height);
  min-width: 0;
  /* Lets the middle row shrink so its own overflow scrolls, rather than pushing the
     player bar past the bottom of the viewport. */
  min-height: 0;
}

.shio-shell__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--shio-space-4);
  padding: 0 var(--shio-space-4);
  background: hsl(var(--shio-bg-panel));
  border-bottom: var(--shio-border-width) solid hsl(var(--shio-border));
  position: sticky;
  top: 0;
  z-index: var(--shio-z-sticky);
}

.shio-shell__identity {
  display: flex;
  align-items: baseline;
  gap: var(--shio-space-3);
  min-width: 0;
}

.shio-shell__mark {
  font-size: var(--shio-type-card-title-size);
}

.shio-shell__signout {
  padding-inline: var(--shio-space-2);
  color: hsl(var(--shio-text-muted));
}

.shio-shell__signout:hover {
  color: hsl(var(--shio-text-primary));
}

.shio-shell__user {
  display: flex;
  align-items: center;
  gap: var(--shio-space-2);
}

.shio-shell__avatar {
  display: grid;
  place-items: center;
  width: 28px;
  height: 28px;
  border-radius: 999px;
  background: hsl(var(--shio-accent-primary) / 0.18);
  border: 1px solid hsl(var(--shio-accent-primary) / 0.45);
  font-family: var(--shio-font-display);
  font-size: var(--shio-type-body-sm-size);
  color: hsl(var(--shio-accent-primary));
}

.shio-shell__who {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.15;
}

.shio-shell__name {
  font-size: var(--shio-type-body-sm-size);
}

.shio-shell__role {
  font-size: var(--shio-type-meta-size);
  color: hsl(var(--shio-text-muted));
  text-transform: capitalize;
}

/* On a narrow header the clock keeps its place and the name gives way: the time is what
   is read constantly, and the avatar still identifies the account. */
@media (max-width: 720px) {
  .shio-shell__who {
    display: none;
  }
}

.shio-shell__state {
  display: flex;
  align-items: center;
  gap: var(--shio-space-3);
}

.shio-shell__content {
  min-height: 0;
  padding: var(--shio-space-5);
  display: flex;
  flex-direction: column;
  gap: var(--shio-space-4);
  min-width: 0;
  overflow: auto;
}

.shio-shell__player {
  border-top: var(--shio-border-width) solid hsl(var(--shio-border));
  background: hsl(var(--shio-bg-panel));
}

@media (max-width: 1023px) {
  .shio-shell {
    grid-template-columns: 1fr;
  }

  .shio-shell__rail {
    display: none;
  }
}

@media (max-width: 640px) {
  .shio-shell__content {
    padding: var(--shio-space-3);
  }
}
</style>
